import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import { Props } from '.';
import useTranslations from '@hooks/useTranslations';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useGetNotEscapedTranslationMarkup } from '@hooks/useGetNotEscapedTranslationMarkup';
import { Icons } from '@components/Icon';
import { IconTemplate } from '@components/Icon/IconTemplate';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import CarDetailsDuck from 'src/redux/carDetails/carDetails.duck';
import { FEATURES_LIST, FeatureSwitchContext } from '../../context/featureSwitchApp';
import { BANK_CHECKOUT_JOURNEY_TYPES } from '../../constants/enums';
import Spinner from '@components/Spinner';
import { theme } from '../../styles/theme';
import DealService from '../../services/deal/deal.service';
import { getSessionIdCookie } from '@utils/Session.utils';
import { useDispatch } from 'react-redux';
import { usePlausible, UserEvent } from '@hooks/usePlausible';

const CheckoutAssistedFinanceTemplate: FunctionComponent<Props> = ({
    className,
    isConfirmationPage = false,
    isRentJourney = false,
}) => {
    const { t }: any = useTranslations();
    const dispatch = useDispatch();
    const { plausibleUserEvent } = usePlausible();
    const { getNotEscapedTranslationMarkup } = useGetNotEscapedTranslationMarkup();

    const { currentDeal } = useCarDetailsDuck();

    const isAssistedEnabled = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_BANK_CHECKOUT_JOURNEY_TYPES,
        BANK_CHECKOUT_JOURNEY_TYPES.ASSISTED
    );

    const isDirectEnabled = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_BANK_CHECKOUT_JOURNEY_TYPES,
        BANK_CHECKOUT_JOURNEY_TYPES.DIRECT
    );

    const defaultBankJourney =
        useContext(FeatureSwitchContext)?.FEATURE_SWITCH_BANK_CHECKOUT_DEFAULT_JOURNEY_TYPE ?? '';

    const [isLoading, setIsLoading] = useState(false);
    const [selectedBankJourney, setSelectedBankJourney] = useState(
        defaultBankJourney || BANK_CHECKOUT_JOURNEY_TYPES.ASSISTED
    );

    useEffect(() => {
        CarDetailsDuck.setAssistedFinance(defaultBankJourney === BANK_CHECKOUT_JOURNEY_TYPES.ASSISTED);
    }, []);

    const handleSelectClick = async (value: BANK_CHECKOUT_JOURNEY_TYPES) => {
        setIsLoading(true);

        setSelectedBankJourney(value);

        const response = await DealService.updateDeal(
            {
                extra_field: { assistedFinance: value === BANK_CHECKOUT_JOURNEY_TYPES.ASSISTED },
            },
            getSessionIdCookie(),
            false
        );

        dispatch(CarDetailsDuck.setCurrentDeal(response.data?.deal));
        setIsLoading(false);
    };

    return (
        <div className={className}>
            <div className="description">
                <p
                    className="descriptionBox1"
                    dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                        `checkout.assisted.finance.description1${isConfirmationPage ? '.confirmation' : ''}${
                            isRentJourney ? '.rent' : ''
                        }`
                    )}
                />
                <p
                    className="descriptionBox2"
                    dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                        `checkout.assisted.finance.description2${isConfirmationPage ? '.confirmation' : ''}${
                            isRentJourney ? '.rent' : ''
                        }`
                    )}
                />
            </div>
            {isLoading && <Spinner size={25} border={3} color={theme.colors.black} />}
            {isAssistedEnabled && isDirectEnabled && !isConfirmationPage && (
                <div className={`select ${isLoading ? 'isLoading' : ''}`}>
                    <div
                        data-testid="assistedFinanceSection"
                        id="assistedFinance"
                        onClick={() => {
                            plausibleUserEvent(UserEvent.SelectAssistedBankJourneyType);
                            handleSelectClick(BANK_CHECKOUT_JOURNEY_TYPES.ASSISTED);
                        }}
                        className={`selectLeft ${
                            selectedBankJourney === BANK_CHECKOUT_JOURNEY_TYPES.ASSISTED ? 'selected' : ''
                        }`}
                    >
                        <span
                            className={`selectIcon ${
                                selectedBankJourney === BANK_CHECKOUT_JOURNEY_TYPES.ASSISTED ? 'selected' : ''
                            }`}
                        >
                            {selectedBankJourney === BANK_CHECKOUT_JOURNEY_TYPES.ASSISTED && (
                                <span data-testid="assistedFinanceSelected" className="selected" />
                            )}
                        </span>
                        <IconTemplate className="icon" name={Icons.ProfileCall} />
                        <span className="selectTitle">{t('checkout.preOrder.finance.assisted.title')}</span>
                        <p className="selectDescription">{t('checkout.preOrder.finance.assisted.description')}</p>
                    </div>
                    <div
                        onClick={() => {
                            plausibleUserEvent(UserEvent.SelectDirectBankJourneyType);
                            handleSelectClick(BANK_CHECKOUT_JOURNEY_TYPES.DIRECT);
                        }}
                        className={`selectRight ${
                            selectedBankJourney === BANK_CHECKOUT_JOURNEY_TYPES.DIRECT ? 'selected' : ''
                        }`}
                    >
                        <span
                            className={`selectIcon ${
                                selectedBankJourney === BANK_CHECKOUT_JOURNEY_TYPES.DIRECT ? 'selected' : ''
                            }`}
                        >
                            {selectedBankJourney === BANK_CHECKOUT_JOURNEY_TYPES.DIRECT && (
                                <span className="selected" />
                            )}
                        </span>
                        <IconTemplate className="icon" name={Icons.Profile} />
                        <span className="selectTitle">{t('checkout.preOrder.finance.direct.title')}</span>
                        <p className="selectDescription">{t('checkout.preOrder.finance.direct.title')}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CheckoutAssistedFinanceTemplate;
